import type { EnergyMonitoringPeriodId } from 'src/components/EnergyMonitoring/helpers/period_helpers';
import type { EnergyHeatmapMetric } from 'src/components/EnergyMonitoring/helpers/energy_heatmap_helpers';

export type DashEnergyPeriodId = EnergyMonitoringPeriodId;

export type DashEnergyInfoKpiId =
	| 'load'
	| 'solar'
	| 'selfPowered'
	| 'gridImport'
	| 'periodConsumption'
	| 'periodGeneration'
	| 'tariff'
	| 'currentTariff'
	| 'offPeak'
	| 'topConsumer'
	| 'standby'
	| 'peakDemand';

export type DashEnergyWidgetTypes =
	| 'dashEnergyInfo'
	| 'dashEnergyKpiLive'
	| 'dashEnergyKpiPeriod'
	| 'dashEnergyTopConsumers'
	| 'dashEnergyConsumerMix'
	| 'dashEnergyRoomChart'
	| 'dashEnergyFlow'
	| 'dashEnergyHeatmap'
	| 'dashEnergyPeak'
	| 'dashEnergyInsights'
	| 'dashEnergyGeneration'
	| 'dashEnergyPvDistribution'
	| 'dashEnergyTariff'
	| 'dashEnergyPhase'
	| 'dashEnergyEnvironmental'
	| 'dashEnergyDeviceTrend'
	| 'dashEnergyRoomCharts'
	| 'dashEnergyRoomDistribution';

export const DASH_ENERGY_INFO_CATEGORY_ORDER: DashEnergyInfoKpiId[] = [
	'load',
	'solar',
	'selfPowered',
	'gridImport',
	'periodConsumption',
	'periodGeneration',
	'tariff',
	'currentTariff',
	'offPeak',
	'topConsumer',
	'standby',
	'peakDemand',
];

export const DASH_ENERGY_INFO_CATEGORY_META: Record<
	DashEnergyInfoKpiId,
	{
		labelKey: string;
		labelDefault: string;
		descriptionKey: string;
		descriptionDefault: string;
		icon: string;
	}
> = {
	load: {
		labelKey: 'dash_energy_info_live_consumption_proposal',
		labelDefault: 'Live consumption',
		descriptionKey: 'dash_energy_info_live_consumption_info',
		descriptionDefault: 'Real-time home load and grid draw.',
		icon: 'fas fa-bolt',
	},
	solar: {
		labelKey: 'dash_energy_info_active_generation_proposal',
		labelDefault: 'Active generation',
		descriptionKey: 'dash_energy_info_active_generation_info',
		descriptionDefault: 'Current solar or local production.',
		icon: 'fas fa-solar-panel',
	},
	selfPowered: {
		labelKey: 'energy_monitoring_insight_self_powered',
		labelDefault: 'Self-powered',
		descriptionKey: 'dash_energy_info_self_powered_info',
		descriptionDefault: 'Share of current demand covered by solar.',
		icon: 'fas fa-house',
	},
	gridImport: {
		labelKey: 'label_grid_import',
		labelDefault: 'Grid import',
		descriptionKey: 'dash_energy_info_grid_import_info',
		descriptionDefault: 'Power currently imported from the grid.',
		icon: 'fas fa-plug',
	},
	periodConsumption: {
		labelKey: 'energy_monitoring_period_consumption',
		labelDefault: 'Period consumption',
		descriptionKey: 'energy_monitoring_kpi_period_consumption_info',
		descriptionDefault: 'Energy consumed during the selected period.',
		icon: 'fas fa-chart-column',
	},
	periodGeneration: {
		labelKey: 'dash_energy_info_period_generation_proposal',
		labelDefault: 'Period generation',
		descriptionKey: 'energy_monitoring_kpi_period_generation_info',
		descriptionDefault: 'Solar energy generated during the selected period.',
		icon: 'fas fa-sun',
	},
	tariff: {
		labelKey: 'energy_monitoring_period_cost',
		labelDefault: 'Reported cost',
		descriptionKey: 'energy_monitoring_kpi_tariff_info',
		descriptionDefault: 'Electricity cost reported by energy statistics for the selected period.',
		icon: 'fas fa-coins',
	},
	currentTariff: {
		labelKey: 'energy_monitoring_tariff_current',
		labelDefault: 'Current tariff',
		descriptionKey: 'dash_energy_info_current_tariff_info',
		descriptionDefault: 'Tariff and electricity rate active right now.',
		icon: 'fas fa-receipt',
	},
	offPeak: {
		labelKey: 'dash_energy_info_off_peak_proposal',
		labelDefault: 'Off-peak usage',
		descriptionKey: 'energy_monitoring_kpi_off_peak_info',
		descriptionDefault: 'Share of period consumption billed at the lowest configured rate.',
		icon: 'fas fa-moon',
	},
	topConsumer: {
		labelKey: 'dash_energy_info_top_consumer_proposal',
		labelDefault: 'Top consumer now',
		descriptionKey: 'dash_energy_info_top_consumer_info',
		descriptionDefault: 'Device currently drawing the most power.',
		icon: 'fas fa-arrow-trend-up',
	},
	standby: {
		labelKey: 'dash_energy_info_standby_proposal',
		labelDefault: 'Standby consumption',
		descriptionKey: 'dash_energy_info_standby_info',
		descriptionDefault: 'Combined live power from low-consumption devices.',
		icon: 'fas fa-power-off',
	},
	peakDemand: {
		labelKey: 'dash_energy_info_peak_demand_proposal',
		labelDefault: 'Peak consumption',
		descriptionKey: 'energy_monitoring_kpi_peak_demand_info',
		descriptionDefault: 'Highest consumption interval in the selected period.',
		icon: 'fas fa-chart-line',
	},
};

export const DASH_ENERGY_WIDGET_TYPES: DashEnergyWidgetTypes[] = [
	'dashEnergyInfo',
	'dashEnergyKpiLive',
	'dashEnergyKpiPeriod',
	'dashEnergyTopConsumers',
	'dashEnergyConsumerMix',
	'dashEnergyRoomChart',
	'dashEnergyFlow',
	'dashEnergyHeatmap',
	'dashEnergyPeak',
	'dashEnergyInsights',
	'dashEnergyGeneration',
	'dashEnergyPvDistribution',
	'dashEnergyTariff',
	'dashEnergyPhase',
	'dashEnergyEnvironmental',
	'dashEnergyDeviceTrend',
	'dashEnergyRoomCharts',
	'dashEnergyRoomDistribution',
];

/** System dashboards that bypass widget limits and dashboard count caps. */
export const PREDEFINED_DASHBOARD_IDS = [-10, -11, -103, -104, -105, -106] as const;

export interface DashEnergyBaseWidgetConfig {
	period: DashEnergyPeriodId;
	/** Period badge in the widget section header. Hidden only when explicitly disabled. */
	showPeriodLabel?: boolean;
}

/** Selected KPI cards shown side-by-side in one widget. */
export interface DashEnergyInfoWidgetConfig extends DashEnergyBaseWidgetConfig {
	infoCategories: DashEnergyInfoKpiId[];
	showTariffBar: boolean;
}

export type DashEnergyKpiLiveWidgetConfig = DashEnergyBaseWidgetConfig;

export interface DashEnergyKpiPeriodWidgetConfig extends DashEnergyBaseWidgetConfig {
	showTariffBar: boolean;
}

export interface DashEnergyTopConsumersWidgetConfig extends DashEnergyBaseWidgetConfig {
	limit: number;
	groupBy: 'device' | 'room';
}

export interface DashEnergyConsumerMixWidgetConfig extends DashEnergyBaseWidgetConfig {
	minSharePercent: number;
	showLegend: boolean;
}

/** One room per widget instance. */
export interface DashEnergyRoomChartWidgetConfig extends DashEnergyBaseWidgetConfig {
	roomId?: number;
	deviceIds?: string[] | null;
	chartType: 'bar' | 'areaspline';
}

export interface DashEnergyFlowWidgetConfig extends DashEnergyBaseWidgetConfig {
	showSupply: boolean;
	supplyMode: 'auto' | 'measured' | 'calculated';
	showRooms: boolean;
	showDevices: boolean;
	maxRoomNodes: number;
	maxDeviceNodes: number;
}

export type DashEnergyHeatmapVariant = 'full' | 'minimal';

export interface DashEnergyHeatmapWidgetConfig extends DashEnergyBaseWidgetConfig {
	heatmapScope: 'all' | 'room' | 'device';
	heatmapMetric: EnergyHeatmapMetric;
	heatmapVariant: DashEnergyHeatmapVariant;
	allowInWidgetMetricToggle: boolean;
	deviceId?: string;
	roomId?: number;
	showPeriodTotal: boolean;
}

export type DashEnergyPeakVariant = 'overview' | 'byMeter' | 'phaseBalance';

export interface DashEnergyPeakWidgetConfig extends DashEnergyBaseWidgetConfig {
	peakVariant: DashEnergyPeakVariant;
}

export interface DashEnergyDeviceWidgetConfig extends DashEnergyBaseWidgetConfig {
	deviceId?: string;
}

export interface DashEnergyRoomChartsWidgetConfig extends DashEnergyBaseWidgetConfig {
	roomIds?: number[];
}

export interface DashEnergyRoomDistributionWidgetConfig extends DashEnergyBaseWidgetConfig {
	showZeroConsumptionRooms: boolean;
}

/**
 * The parent widget `type` discriminates this map. Config remains a flat persisted object so
 * existing dashboard JSON and legacy widget migrations keep working without a config discriminator.
 */
export interface DashEnergyWidgetConfigMap {
	dashEnergyInfo: DashEnergyInfoWidgetConfig;
	dashEnergyKpiLive: DashEnergyKpiLiveWidgetConfig;
	dashEnergyKpiPeriod: DashEnergyKpiPeriodWidgetConfig;
	dashEnergyTopConsumers: DashEnergyTopConsumersWidgetConfig;
	dashEnergyConsumerMix: DashEnergyConsumerMixWidgetConfig;
	dashEnergyRoomChart: DashEnergyRoomChartWidgetConfig;
	dashEnergyFlow: DashEnergyFlowWidgetConfig;
	dashEnergyHeatmap: DashEnergyHeatmapWidgetConfig;
	dashEnergyPeak: DashEnergyPeakWidgetConfig;
	dashEnergyInsights: DashEnergyBaseWidgetConfig;
	dashEnergyGeneration: DashEnergyBaseWidgetConfig;
	dashEnergyPvDistribution: DashEnergyBaseWidgetConfig;
	dashEnergyTariff: DashEnergyBaseWidgetConfig;
	dashEnergyPhase: DashEnergyBaseWidgetConfig;
	dashEnergyEnvironmental: DashEnergyBaseWidgetConfig;
	dashEnergyDeviceTrend: DashEnergyDeviceWidgetConfig;
	dashEnergyRoomCharts: DashEnergyRoomChartsWidgetConfig;
	dashEnergyRoomDistribution: DashEnergyRoomDistributionWidgetConfig;
}

type LegacyDashEnergyWidgetConfig = DashEnergyBaseWidgetConfig & {
	infoCategories?: DashEnergyInfoKpiId[];
	limit?: number;
	groupBy?: 'device' | 'room';
	minSharePercent?: number;
	showLegend?: boolean;
	roomId?: number;
	deviceIds?: string[] | null;
	chartType?: 'bar' | 'areaspline';
	showGeneration?: boolean;
	showSupply?: boolean;
	supplyMode?: 'auto' | 'measured' | 'calculated';
	showRooms?: boolean;
	showDevices?: boolean;
	maxRoomNodes?: number;
	maxDeviceNodes?: number;
	heatmapScope?: 'all' | 'room' | 'device';
	heatmapMetric?: EnergyHeatmapMetric;
	heatmapVariant?: DashEnergyHeatmapVariant;
	peakVariant?: DashEnergyPeakVariant;
	allowInWidgetMetricToggle?: boolean;
	deviceId?: string;
	roomIds?: number[];
	showPeriodTotal?: boolean;
	showTariffBar?: boolean;
	showPeriodLabel?: boolean;
	showZeroConsumptionRooms?: boolean;
};

/**
 * Pass the parent widget type for a precise config. Omitting it retains the legacy superset for
 * untyped persistence boundaries while callers migrate to the mapped form.
 */
export type DashEnergyWidgetConfig<T extends DashEnergyWidgetTypes = never> = [T] extends [never]
	? LegacyDashEnergyWidgetConfig
	: DashEnergyWidgetConfigMap[T];

export const isDashEnergyWidgetType = (type: string | undefined): type is DashEnergyWidgetTypes =>
	DASH_ENERGY_WIDGET_TYPES.includes(type as DashEnergyWidgetTypes);

export const isPredefinedDashboardId = (id: string | number | undefined | null): boolean => {
	if (id === undefined || id === null || id === '') return false;
	return PREDEFINED_DASHBOARD_IDS.includes(Number(id) as (typeof PREDEFINED_DASHBOARD_IDS)[number]);
};
